/**
 * CommentMediaThumbnail
 *
 * Mirrors Flutter's `commentMediaWidget`
 * (lib/presentation/custom_widgets/comment_widget.dart:545): when a comment is
 * attached to a media (the user replied with a video/image), render a small
 * ratio-preserving thumbnail bounded by 90 x 120, with a play overlay for
 * videos. The box is sized from the media's own `aspectRatio` so a landscape
 * reply is not centre-cropped into a portrait tile — the same media reads the
 * same shape here as in the profile grid (`ContentGridItem`). Tapping the
 * thumbnail navigates to the original media via its relative path.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ContentThumbnail } from '@/presentation/feature/content/components/ContentThumbnail';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { Navigator } from '@/presentation/routing/Navigator';
import { env } from '@/config/env';
import { MediaType } from '@/domain/enums/mediaType';
import { VIDEO_CONSTANTS } from '@/domain/constants/videoConstants';
import {
  COMMENT_MEDIA_THUMBNAIL_MAX_HEIGHT,
  COMMENT_MEDIA_THUMBNAIL_MAX_WIDTH,
} from '@/presentation/constants/contentConstants';
import { getRatioPreservingBoxSize } from '@/presentation/utils/mediaSizing';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { MediaInfoEntity } from '@/domain/entities/info/mediaInfoEntity';

export interface CommentMediaThumbnailProps {
  mediaInfo: MediaInfoEntity | null | undefined;
  /** Owning comment id, used to build the `comment-media-<commentId>` qa id. */
  commentId: string;
}

export const CommentMediaThumbnail: React.FC<CommentMediaThumbnailProps> = ({ mediaInfo, commentId }) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  if (!mediaInfo) return null;

  const isProcessing = !mediaInfo.thumbnail;
  const isVideo = mediaInfo.type === MediaType.VIDEO;

  // Size the box from the media's own ratio (bounded by the 90x120 envelope) so
  // the inner `object-fit: cover` never crops. Fallback mirrors Flutter's
  // `MediaInfo({ this.aspectRatio = DimensionConstants.videoPlayerAspectRatioPortrait })`
  // (lib/data/models/media_info.dart:34-37 → lib/domain/constants/dimensions_constants.dart:102).
  const { width, height } = getRatioPreservingBoxSize({
    aspectRatio: mediaInfo.aspectRatio,
    fallbackAspectRatio: VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO,
    maxWidth: COMMENT_MEDIA_THUMBNAIL_MAX_WIDTH,
    maxHeight: COMMENT_MEDIA_THUMBNAIL_MAX_HEIGHT,
  });

  const onClick = () => {
    if (isProcessing) return;
    Navigator.navigateToContentDetailsByPath(mediaInfo.path);
  };

  return (
    <div
      {...qaAttr({ id: `comment-media-${commentId}` })}
      onClick={onClick}
      style={{
        width,
        height,
        marginTop: responsive.spacing.sm,
        borderRadius: responsive.borderRadius.md,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: theme.backgroundSecondary,
        cursor: isProcessing ? 'default' : 'pointer',
        flexShrink: 0,
      }}
    >
      {isProcessing ? (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.text.primary,
            fontSize: responsive.font.xs,
            fontStyle: 'italic',
            textAlign: 'center',
            padding: responsive.spacing.xs,
          }}
        >
          {l10n.comments.mediaProcessing}
        </div>
      ) : (
        <>
          <ContentThumbnail
            thumbnailUrl={`${env.CDN_NAME}${mediaInfo.thumbnail}`}
          />
          {isVideo && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: responsive.borderRadius.round,
                  backgroundColor: theme.loadingOverlay,
                  padding: responsive.spacing.sm,
                }}
              >
                <AssetIcon
                  path="/assets/vectors/play_filled.svg"
                  width={responsive.icon.xlarge}
                  height={responsive.icon.xlarge}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
